import {CreatePeerPracticeSessionDialogStateService} from "../../service/create-peer-practice-session-dialog-state.service";
import {Component, computed, effect, inject, input, linkedSignal, signal} from "@angular/core";
import {MatDivider} from "@angular/material/divider";
import {ListComponent} from "../../../../../../../shared/shared-components/list/list.component";
import {FetchDataType, Scenario} from "@models";
import {ListItemComponent} from "@common/ui/list-item/list-item.component";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {SharedModule} from "../../../../../../../shared/shared.module";
import {Translations} from "@translations";
import {UntilDestroy} from "@ngneat/until-destroy";
import {ComponentLoadingContainerComponent} from "../../../../../../../shared/shared-components/component-loading-container/component-loading-container.component";
import {toSignal} from "@angular/core/rxjs-interop";
import {LabelledContainerComponent} from "@common/ui/labelled-container/labelled-container.component";
import {InfoHoverComponent} from "../../../../../../../shared/shared-components/info-hover/info-hover.component";

@UntilDestroy()
@Component({
	selector: "sim-peer-practice-session-step1",
	standalone: true,
	imports: [
		MatDivider,
		ListComponent,
		ListItemComponent,
		MatIconButton,
		MatIcon,
		SharedModule,
		ComponentLoadingContainerComponent,
		LabelledContainerComponent,
		InfoHoverComponent,
	],
	templateUrl: "./peer-practice-session-step1.component.html",
	styleUrl: "./peer-practice-session-step1.component.scss",
})
export class PeerPracticeSessionStep1Component {
	// I/O
	public readonly eventStartDate = input.required<Date>();
	public readonly eventEndDate = input.required<Date>();

	// Properties
	public readonly isFormCooldown = signal<boolean>(false);
	public readonly isLoadingForms = signal<boolean>(false);
	public readonly activeStationForm = computed(() => this.computeCurrentStationForm(this.activeStationIndex(), this.arrayStatusChange()));

	// Template Constants
	protected readonly FetchDataType = FetchDataType;
	protected readonly T = Translations;

	// Injections
	readonly #stateService = inject(CreatePeerPracticeSessionDialogStateService);

	// Derived Properties
	protected readonly activeStationIndex = this.#stateService.activeStationIndex;
	protected readonly arrayStatusChange = toSignal(this.#stateService.stationForms.statusChanges);
	protected readonly excludeRoomIds = computed<string[]>(() => this.#stateService.session()?.stations.map((s) => s.roomId) ?? []);
	protected readonly excludeScenarioIds = linkedSignal<string[]>(() => this.activeStationForm().value.scenarios?.map((x) => x.id) ?? []);

	// Form
	public readonly metadataForm = this.#stateService.metadataForm;
	public readonly stationFormArray = this.#stateService.stationForms;

	protected selectStation(index: number): void {
		this.isFormCooldown.set(true);
		this.#stateService.setCurrentStationIndex(index);

		setTimeout(() => {
			this.isFormCooldown.set(false);
		}, 300);
	}

	protected addStation() {
		this.#stateService.addStationForm();
		this.selectStation(this.stationFormArray.length - 1);
	}

	protected deleteStation(index: number): void {
		this.#stateService.deleteStation(index);
	}

	protected addScenario(scenario: Scenario) {
		const scenarios = this.activeStationForm()?.value.scenarios;
		this.activeStationForm()?.controls.scenarios.setValue([...(scenarios ?? []), scenario]);
	}

	protected removeScenario(scenario: Scenario) {
		let scenarios = this.activeStationForm()?.value.scenarios;
		scenarios = scenarios?.filter((s) => s.id !== scenario.id);
		this.activeStationForm()?.controls.scenarios.setValue(scenarios ?? []);
	}

	private computeCurrentStationForm(number: number, _: unknown) {
		return this.stationFormArray.at(number);
	}

	readonly #observeScenarioIds = effect((onCleanup) => {
		const sub = this.#stateService.stationForms.valueChanges.subscribe((value) => {
			const relevantValue = value.at(this.activeStationIndex());
			this.excludeScenarioIds.set(relevantValue?.scenarios?.map((x) => x.id) ?? []);
		});
		onCleanup(() => sub.unsubscribe());
	});
}
